import React, { useRef, useEffect, useState } from "react";
import { ScrollArea } from "@/components/ui/scroll-area"; // Import ScrollArea
import { useAppStore } from "@/hooks/useAppStore";
import { MessageBubble } from "../MessageBubble";
import { StampBubble } from "../StampBubble";
import { GameBubble } from "../GameBubble";
import { DisplayMessage, SystemMessageData, StampMessage, GameMessage } from "@/types";
import { UserStatusBubble } from "../UserStatusBubble";
import { CommentTranslationDialog } from "../../dialogs/CommentTranslationDialog";
import { toast } from "sonner";
import { useTranslation } from "@/lib/translate";
import { apiFetch } from "@/lib/fetch";

export type FilterType = "showOnlyOthers" | "showOnlyMine" | "none";
// chat text size
export type TextSize = "base" | "lg" | "xl" | "2xl" | "4xl" | "6xl";

interface Props {
  roomId: string;
  filter?: FilterType;
  showOriginalText?: boolean;
  textSize?: TextSize;
  isPresentationMode?: boolean;
};

export const AdminChatArea: React.FC<Props> = ({
  filter = "none",
  showOriginalText = false,
  textSize = "base",
  isPresentationMode = false,
  roomId,
}) => {
  const isReady = useAppStore((state) => state.isReady);
  const messages = useAppStore((state) => state.messages);
  const preferredLanguage = useAppStore((state) => state.preferredLanguage);
  const readAllMessages = useAppStore((state) => state.readAllMessages);
  const countUnreadMessages = useAppStore((state) => state.countUnreadMessages);
  const viewportRef = useRef<HTMLDivElement>(null);
  const translatedMessage = useAppStore((state) => state.translatedMessage);
  const isAutoTTS = useAppStore((state) => state.isAutoTTS);
  const bottomElementRef = useRef<HTMLDivElement>(null);
  const scrollViewComponentRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [commentTranslationDialogOpen, setCommentTranslationDialogOpen] = React.useState(false);
  const [commentContent, setCommentContent] = React.useState<{ id: string, comment: string, commentType: string, email: string, expectedTranscription?: string, expectedTranslation?: string, currentTranscription?: string, translations?: Record<string, string>, targetLanguage?: string }>({ id: "", comment: "", commentType: "", email: "", expectedTranscription: "", expectedTranslation: "", currentTranscription: "", translations: {}, targetLanguage: "" });
  const shouldAutoScrollRef = useRef<boolean>(true);
  const currentUnreadMessageQty = countUnreadMessages() || 0;
  const setMessagePopupOpen = useAppStore((state) => state.setMessagePopupOpen);
  const setFocusedMessageId = useAppStore((state) => state.setFocusedMessageId);

  const t = useTranslation(preferredLanguage);

  // Whenever messages change, scroll to the top (or bottom in presentation mode)
  const SCROLL_FORGIVENESS = 200;

  useEffect(() => {
    const scrollContainer = scrollViewComponentRef.current;
    if (shouldAutoScrollRef.current && isReady && scrollViewComponentRef.current) {
      if (currentUnreadMessageQty > 0) {
        readAllMessages();
      }

      const scrollTop = scrollViewComponentRef.current.scrollHeight;
      scrollViewComponentRef.current.scrollTo({ top: scrollTop, behavior: "instant" });
    }

    // Respond to scroll events to clear messages read
    const handleScroll = () => {
      if (!scrollContainer || !bottomElementRef.current) {
        return;
      }
      const { scrollTop } = scrollContainer;
      const isAtBottom = scrollTop >= scrollContainer.scrollHeight - scrollContainer.clientHeight - SCROLL_FORGIVENESS;

      if (isAtBottom && currentUnreadMessageQty > 0) {
        readAllMessages();
      }

      // Only after scrolling will we disable autoscroll depending on how close to the bottom we are
      shouldAutoScrollRef.current = isAtBottom;
    };

    // Add scroll event listener
    scrollContainer?.addEventListener("scroll", handleScroll);

    // Cleanup event listener on unmount
    return () => {
      scrollContainer?.removeEventListener("scroll", handleScroll);
    };
  }, [messages, isPresentationMode, showOriginalText, textSize, filter, currentUnreadMessageQty]);

  // Initial scroll to bottom when component is ready
  useEffect(() => {
    if (isReady && scrollViewComponentRef.current) {
      if (currentUnreadMessageQty > 0) {
        readAllMessages();
      }
      const scrollTop = scrollViewComponentRef.current.scrollHeight;
      scrollViewComponentRef.current.scrollTo({ top: scrollTop, behavior: "instant" });
    }
  }, [isReady, isPresentationMode]);

  let displayMessages = messages;
  if (filter == "showOnlyMine") {
    displayMessages = messages.filter(msg => (msg as any)?.isMe);
  } else if (filter == "showOnlyOthers") {
    displayMessages = messages.filter(msg => !(msg as any)?.isMe);
  }

  useEffect(() => {
    if (error) {
      toast(error);
    }
  }, [error]);

  useEffect(() => {
    if (!translatedMessage) {
      return;
    }
    const translatedText = Object.values(translatedMessage.translations).join("");
    console.log("translatedMessage", translatedText);
    translatedText && isAutoTTS && playAudio(translatedText);
  }, [translatedMessage]);

  const playAudio = async (text: string) => {
    setError(null);
    setLoading(true);
    try {
      const res = await apiFetch(`/tts?text=${encodeURIComponent(text)}`);
      const contentType = res.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        const errorData = await res.json();
        setError("TTS request failed: " + (errorData.detail || "unknown error"));
        return;
      }
      if (!res.ok) {
        setError(`Request failed with status ${res.status}`);
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setAudioUrl(url);
    } catch (e) {
      console.error(`Request failed with error ${e}`);
      setError("Failed to connect to server");
    } finally {
      setLoading(false);
    }
  }

  const setAudioUrl = async (url: any) => {
    if (!audioRef.current) {
      return
    }
    audioRef.current.src = url;
    audioRef.current.play().catch(err => {
      console.error("Playback failed:", err);
    })
  }

  return (
    <>
      <ScrollArea className="flex-1 custom-scrollbar min-h-24" type="auto" viewPortRef={scrollViewComponentRef}>
        {" "}
        <audio
          ref={audioRef}
          hidden
          onWaiting={() => setLoading(true)}
          onCanPlay={() => setLoading(false)}
          onPlaying={() => {
            setPlaying(true)
            setLoading(false)
          }}
          onEnded={() => {
            setPlaying(false);
            setLoading(false);
          }}
        />
        <div ref={viewportRef} className="h-full p-2 md:p-4">
          {/* Scrollable content */}
          {[...displayMessages].reverse().map((msg: DisplayMessage | SystemMessageData | StampMessage | GameMessage) => (<>
            {msg.type == "message" && <MessageBubble
              key={msg.id}
              roomId={roomId}
              message={msg as DisplayMessage}
              preferredLanguage={preferredLanguage}
              filter={filter}
              textSize={textSize}
              hideOriginalText={filter == "showOnlyMine" && !showOriginalText}
              setCommentTranslationDialogOpen={setCommentTranslationDialogOpen}
              setCommentContent={setCommentContent}
              onDoubleClick={() => {
                setMessagePopupOpen(true);
                setFocusedMessageId(msg.timestamp);
              }}
              presentationMode={isPresentationMode}
            />}
            {msg.type == "game" && <GameBubble
              key={msg.session}
              message={msg as GameMessage}
              preferredLanguage={preferredLanguage}
            />}
            {msg.type == "system" && (msg.message == "JOIN" || msg.message == "LEAVE") && <UserStatusBubble
              key={Date.now()}
              data={msg} preferredLanguage={preferredLanguage} />
            }
            {msg.type == "stamp" && <StampBubble
              key={msg.id}
              message={msg as StampMessage}
              preferredLanguage={preferredLanguage}
            />}
          </>
          ))}
          {roomId && messages.length === 0 && (
            <div className="flex h-full items-center justify-center">
              <p className="text-foreground">
                {isReady ? t("There are no messages in the room..") : t("Loading room contents...")}
              </p>
            </div>
          )}
          {!roomId && (
            <div className="flex h-full items-center justify-center">
              <p className="text-foreground">
                {t("Enter Room Id above to inspect the room.")}
              </p>
            </div>
          )}
        </div>
        <div ref={bottomElementRef} /> {/* Reference for using scrollIntoView */}
        {/* <ScrollBar orientation="vertical" /> */}{" "}
        {/* Optional visible scrollbar */}

        {/* If new messages are added, and the user is not at top, show a "New Messages" button */}
        {currentUnreadMessageQty !== 0 && (
          <div
            className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-primary text-primary-foreground px-4 py-2 rounded-full shadow-lg cursor-pointer text-xs z-10 hover:bg-primary/80"
            onClick={() => {
              // Scroll to bottom
              scrollViewComponentRef.current?.scrollTo({ top: scrollViewComponentRef.current.scrollHeight, behavior: "smooth" });
            }}
          >
            {currentUnreadMessageQty} new {currentUnreadMessageQty > 1 ? "messages" : "message"}
          </div>
        )}
      </ScrollArea>

      {commentTranslationDialogOpen && <CommentTranslationDialog
        open={commentTranslationDialogOpen}
        onOpenChange={(open) => {
          setCommentTranslationDialogOpen(open)
          if (!open) { setCommentContent({ id: "", comment: "", commentType: "", email: "", expectedTranscription: "", expectedTranslation: "", currentTranscription: "", translations: {}, targetLanguage: "" }) }
        }}
        commentContent={commentContent}
        setCommentContent={setCommentContent}
      />}
    </>
  );
};
